// src/pages/admin/AdminStaffPermissions.js
// ============================================================================
// ACCÈS DU PERSONNEL — Maxula
// Livré par AbodyStudio Limited — https://abodystudio.com
//
// Deux temps, volontairement separes a l'ecran :
//   1. ON COCHE UN ROLE  -> les permissions se pre-remplissent
//   2. ON COCHE SES MARCHANDS -> il ne verra que ceux-la
//
// Le role n'est qu'un raccourci de saisie : apres l'avoir choisi, chaque case
// reste modifiable. Rien n'est recalcule a partir du role plus tard, donc
// ajuster une case ne sera pas ecrase au prochain enregistrement.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiShield, FiSearch, FiSave, FiUserX, FiLoader, FiAlertTriangle,
  FiCheckSquare, FiSquare, FiUsers, FiBriefcase
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import StaffPerformancePanel from '../../components/admin/StaffPerformancePanel';

const AdminStaffPermissions = () => {
  const [catalog, setCatalog] = useState({ groups: [], presets: [] });
  const [staff, setStaff] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [perms, setPerms] = useState([]);
  const [preset, setPreset] = useState('');
  const [merchantIds, setMerchantIds] = useState([]);
  const [merchantQuery, setMerchantQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- chargement initial ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, list, merch] = await Promise.all([
        API.get('/admin/staff/catalog'),
        API.get('/admin/staff'),
        API.get('/admin/staff/merchants'),
      ]);
      setCatalog(cat?.data?.data || { groups: [], presets: [] });
      setStaff(list?.data?.data || []);
      setMerchants(merch?.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- ouverture d'un compte ----
  const openStaff = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const res = await API.get(`/admin/staff/${id}`);
      const d = res?.data?.data;
      setDetail(d);
      setPerms(Array.isArray(d?.permissions) ? d.permissions : []);
      setPreset(d?.staffRole || '');
      setMerchantIds(Array.isArray(d?.merchantIds) ? d.merchantIds : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Compte introuvable');
      setSelectedId(null);
    }
  };

  // Choisir un role RECOPIE ses permissions. Le message le dit explicitement :
  // sans avertissement, un admin qui clique un role par curiosite ecraserait
  // des ajustements faits a la main sans s'en rendre compte.
  const applyPreset = (key) => {
    setPreset(key);
    const p = catalog.presets.find((x) => x.key === key);
    if (p) {
      setPerms([...p.permissions]);
      toast.info(`Droits du rôle « ${p.label} » appliqués — ajustez les cases si besoin.`);
    }
  };

  const togglePerm = (key) => {
    setPerms((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
    setPreset(''); // on s'ecarte du role : on ne pretend plus le suivre
  };

  const toggleGroup = (group) => {
    const keys = group.items.map((i) => i.key);
    const allOn = keys.every((k) => perms.includes(k));
    setPerms((prev) => (allOn ? prev.filter((p) => !keys.includes(p)) : [...new Set([...prev, ...keys])]));
    setPreset('');
  };

  const toggleMerchant = (id) => {
    setMerchantIds((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const visibleMerchants = useMemo(() => {
    const q = merchantQuery.trim().toLowerCase();
    if (!q) return merchants;
    return merchants.filter((m) => String(m.businessName || '').toLowerCase().includes(q));
  }, [merchants, merchantQuery]);

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await API.put(`/admin/staff/${selectedId}`, {
        preset: preset || undefined,
        permissions: perms,
        merchantIds,
      });
      toast.success('Accès enregistrés');
      await loadAll();
      await openStaff(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!selectedId) return;
    if (!window.confirm("Retirer tous les accès de ce compte ? Il redevient un simple client, son historique est conservé.")) return;
    try {
      await API.delete(`/admin/staff/${selectedId}/access`);
      toast.success('Accès retirés');
      setSelectedId(null);
      setDetail(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
        <FiLoader className="animate-spin" /> Chargement…
      </div>
    );
  }

  const isAdminAccount = String(detail?.role || '') === 'admin';

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <FiShield className="text-[#e11d7b]" size={22} />
        <h1 className="text-2xl font-bold">Accès du personnel</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Choisissez un rôle, ajustez les cases, puis cochez les marchands suivis.
      </p>

      {/* Le rendement se lit ICI, au-dessus de la répartition : c'est en voyant
          qu'un commercial a 2 marchands et un autre 18 qu'on sait quoi changer
          dans les cases du dessous. Séparer les deux écrans obligerait à faire
          l'aller-retour de mémoire. */}
      <StaffPerformancePanel />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---------- COLONNE 1 : les comptes ---------- */}
        <div className="bg-white rounded-lg shadow p-4 lg:col-span-1">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2">
            <FiUsers size={15} /> Comptes ({staff.length})
          </div>
          <div className="divide-y max-h-[70vh] overflow-y-auto">
            {staff.length === 0 ? (
              <p className="text-sm text-gray-400 py-3">
                Aucun compte. Passez un utilisateur en « staff » depuis Gestion des Utilisateurs.
              </p>
            ) : staff.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => openStaff(u.id)}
                className={`w-full text-left py-2.5 px-2 rounded transition ${
                  selectedId === u.id ? 'bg-pink-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-sm font-semibold text-gray-800">
                  {u.firstName} {u.lastName}
                </div>
                <div className="text-xs text-gray-500">{u.email}</div>
                <div className="text-xs mt-1 flex flex-wrap gap-1.5">
                  {u.role === 'admin' ? (
                    <span className="px-2 py-0.5 rounded-full bg-gray-800 text-white font-semibold">
                      Administrateur
                    </span>
                  ) : (
                    <>
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {catalog.presets.find((p) => p.key === u.staffRole)?.label || 'Personnalisé'}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {u.permissions?.length || 0} droits
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {u.merchantCount} marchand{u.merchantCount > 1 ? 's' : ''}
                      </span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ---------- COLONNE 2-3 : le détail ---------- */}
        <div className="lg:col-span-2 space-y-6">
          {!detail ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400 text-sm">
              Sélectionnez un compte à gauche.
            </div>
          ) : isAdminAccount ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start gap-3">
                <FiAlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-gray-700">
                  <strong>{detail.firstName} {detail.lastName}</strong> est administrateur : il a tous
                  les droits et ne se règle pas ici. Pour lui retirer des accès, changez son rôle
                  depuis Gestion des Utilisateurs.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* --- rôle --- */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm font-semibold mb-1">1. Rôle</div>
                <p className="text-xs text-gray-500 mb-3">
                  Un raccourci : cliquer un rôle coche ses droits d'un coup. Vous pouvez ensuite
                  ajuster librement.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {catalog.presets.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p.key)}
                      className={`text-left border rounded-lg p-3 transition ${
                        preset === p.key
                          ? 'border-[#e11d7b] bg-pink-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-sm font-bold flex items-center gap-2">
                        {p.label}
                        {p.scoped ? (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                            portefeuille
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.description}</div>
                    </button>
                  ))}
                </div>
                {!preset && (
                  <p className="text-xs text-gray-400 mt-2">
                    Droits personnalisés — ne correspondent à aucun rôle prédéfini.
                  </p>
                )}
              </div>

              {/* --- permissions --- */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm font-semibold mb-3">
                  2. Ce qu'il peut faire <span className="text-gray-400 font-normal">({perms.length} droits)</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {catalog.groups.map((g) => {
                    const allOn = g.items.every((i) => perms.includes(i.key));
                    return (
                      <div key={g.key} className="border rounded-lg p-3">
                        <button
                          type="button"
                          onClick={() => toggleGroup(g)}
                          className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5 hover:text-[#e11d7b]"
                        >
                          {allOn ? <FiCheckSquare size={13} /> : <FiSquare size={13} />} {g.label}
                        </button>
                        <div className="space-y-1.5">
                          {g.items.map((i) => (
                            <label key={i.key} className="flex items-start gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={perms.includes(i.key)}
                                onChange={() => togglePerm(i.key)}
                                className="mt-0.5"
                              />
                              <span className={i.danger ? 'text-red-700' : 'text-gray-700'}>
                                {i.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  En rouge : les droits sensibles. Tout ce qui n'est pas coché est refusé.
                </p>
              </div>

              {/* --- portefeuille --- */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm font-semibold mb-1 flex items-center gap-2">
                  <FiBriefcase size={15} /> 3. Ses marchands
                  <span className="text-gray-400 font-normal">({merchantIds.length} coché{merchantIds.length > 1 ? 's' : ''})</span>
                </div>

                {/* La règle est écrite à l'écran, pas seulement dans la doc :
                    c'est elle qui décide de ce que la personne verra. */}
                <div className={`text-xs rounded-lg px-3 py-2 mb-3 ${
                  merchantIds.length
                    ? 'bg-blue-50 text-blue-800'
                    : 'bg-amber-50 text-amber-800'
                }`}>
                  {merchantIds.length ? (
                    <>Il ne verra que ces {merchantIds.length} marchand{merchantIds.length > 1 ? 's' : ''}, leurs annonces et leurs commandes.</>
                  ) : (
                    <><strong>Aucun marchand coché</strong> — il verra tous les marchands que ses droits autorisent. Cochez-en pour le limiter à un portefeuille.</>
                  )}
                </div>

                <div className="relative mb-2">
                  <FiSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={merchantQuery}
                    onChange={(e) => setMerchantQuery(e.target.value)}
                    placeholder="Filtrer les marchands…"
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                  />
                </div>

                <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                  {visibleMerchants.length === 0 ? (
                    <p className="p-3 text-sm text-gray-400">Aucun marchand</p>
                  ) : visibleMerchants.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={merchantIds.includes(m.id)}
                        onChange={() => toggleMerchant(m.id)}
                      />
                      <span className="text-sm text-gray-800 flex-1 truncate">{m.businessName}</span>
                      <span className="text-xs text-gray-400">{m.city || ''}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* --- actions --- */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={revoke}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  <FiUserX size={15} /> Retirer tous les accès
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  <FiSave size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStaffPermissions;
